import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, toast } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp, getNodeTestProps } from '@/utils';
import {
    renderWithKeyShortcuts,
    ShortcutHint,
    SHIFT_SHORTCUT,
    useShortcut,
} from '@/components/shortcuts';
import { useShiftActionButton } from './useShiftActionButton';
import { ShiftModal } from './ShiftModal';

const renderShiftButton = renderWithKeyShortcuts(
    'button',
    SHIFT_SHORTCUT.combination,
);

/**
 * The prominent navbar shift control: a solid "Open Shift" CTA while closed, an
 * outline "Close Shift" button plus a `success` status chip while open. Pressing
 * it opens the blind-count modal (rendered here, fed by `useShiftFlow`). Sits in
 * the top bar's left cluster where the static shift chip used to be; gating the
 * actual sale lives in the cart/payment hooks reading `useShift()`.
 *
 * "Open Shift" is pending and disabled until the current shift is known
 * (SEL-49). The sale point handover guard (SEL-30) holds it too: pending while
 * the device's last shift is checked, disabled with a `warning` chip naming
 * its owner while that shift is still open.
 *
 * `F10` opens/closes the shift from anywhere on the sale screen (bos-seller#60):
 * `flow.openModal` already picks open vs. close from the current status, the
 * same function the button itself presses. While the status loads it does
 * nothing; while the handover guard holds opening it shows the gate notice.
 * The button is home while no shift is open (`useShiftActionButton`), and
 * names `F10` with `aria-keyshortcuts`; its hint stays out of its name.
 */
export const ShiftActionButton: React.FC = () => {
    const { t } = useTranslation('pages');
    const {
        flow,
        isOpen,
        handover,
        buttonRef,
        isPending,
        isUnavailable,
        unavailableNotice,
    } = useShiftActionButton();

    useShortcut(SHIFT_SHORTCUT.id, flow.openModal, {
        enabled: !isUnavailable,
        onUnavailable: () => {
            if (unavailableNotice) {
                toast.danger(
                    t(unavailableNotice.key, unavailableNotice.values),
                );
            }
        },
    });

    return (
        <>
            <div className="flex items-center gap-2">
                {isOpen ? (
                    <>
                        <Button
                            {...getNodeTestProps('seller-action-shift')}
                            onPress={flow.openModal}
                            ref={buttonRef}
                            render={renderShiftButton}
                            variant="outline"
                        >
                            <Icon
                                className="size-4"
                                icon="solar:stop-circle-bold-duotone"
                            />
                            {t('seller.shift.close')}
                            <ShortcutHint
                                placement="control"
                                shortcut={SHIFT_SHORTCUT}
                            />
                        </Button>
                        <Chip
                            color="success"
                            {...getNodeTestProps('seller-shift-status')}
                            variant="soft"
                        >
                            {t('seller.shift.statusOpen')}
                        </Chip>
                    </>
                ) : (
                    <>
                        <Button
                            {...getNodeTestProps('seller-action-shift')}
                            isDisabled={isUnavailable}
                            isPending={isPending}
                            onPress={flow.openModal}
                            ref={buttonRef}
                            render={renderShiftButton}
                            variant="primary"
                        >
                            <Icon
                                className="size-4"
                                icon="solar:play-circle-bold-duotone"
                            />
                            {t('seller.shift.open')}
                            <ShortcutHint
                                placement="control"
                                shortcut={SHIFT_SHORTCUT}
                            />
                        </Button>
                        {handover?.state === 'blocked' ? (
                            <Chip
                                color="warning"
                                {...getNodeTestIdProp('seller-shift-handover')}
                                variant="soft"
                            >
                                {t('seller.shift.handoverChip', {
                                    name: handover.sellerName,
                                })}
                            </Chip>
                        ) : null}
                    </>
                )}
            </div>
            <ShiftModal {...flow} />
        </>
    );
};
